// Native (iOS + Android) mix-cover substitution.
//
// For legal reasons, native clients can render the FIRST tracklist cover
// instead of a mix's own artwork. The behavior is owned by the server flag
// NATIVE_MIX_COVER_MODE (served via GET /v1/config/lazy-resolve) so it flips
// without an app release. This module mirrors the proven lazy-config poll in
// MixPlayer.js: a module-level mode + a visibility-driven poll + a subscriber
// hook that forces re-render when the mode changes.
//
// CRITICAL: this is a NATIVE-ONLY concern. On web, desktop, and mobile web
// `isNative` is false and resolveMixCover() returns the mix's original cover
// unchanged — those platforms are bit-identical to before.

import { useEffect, useState } from 'react';
import { isNative } from './webOnly';

const LS_KEY = 'native:mixCoverMode';
const POLL_MS = 5 * 60 * 1000;
const VALID = new Set(['ORIGINAL', 'TRACKLIST_FALLBACK', 'TRACKLIST_STRICT']);

function _readPersisted() {
    try {
        const v = localStorage.getItem(LS_KEY);
        return VALID.has(v) ? v : null;
    } catch { return null; }
}

// Last-known mode, persisted across launches so there's no original-cover
// flash on native after the first run. null until the first-ever poll.
let _mode = _readPersisted();
let _loaded = false;
const _subs = new Set();

function _notify() {
    for (const fn of _subs) { try { fn(); } catch { /* one subscriber must not break the rest */ } }
}

async function _pollOnce() {
    if (!isNative) return;
    if (typeof window === 'undefined' || !window.electron?.djpal?.getClientConfig) return;
    try {
        const cfg = await window.electron.djpal.getClientConfig();
        // music-info wraps payloads in a { data, meta } envelope; tolerate both.
        const root = cfg?.data || cfg;
        const next = root?.nativeMixCover?.mode;
        const mode = VALID.has(next) ? next : 'ORIGINAL';
        const changed = mode !== _mode;
        _mode = mode;
        try { localStorage.setItem(LS_KEY, mode); } catch { /* non-fatal */ }
        if (changed) _notify();
    } catch { /* keep previous value; next poll retries */ }
    finally {
        if (!_loaded) { _loaded = true; _notify(); }
    }
}

// Kick off on native at module load, then re-poll on refocus (same cadence as
// the lazy-config poll). Entirely inert on web/desktop/mobile-web.
if (isNative && typeof window !== 'undefined') {
    _pollOnce();
    if (typeof document !== 'undefined') {
        let _lastPollAt = Date.now();
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return;
            const now = Date.now();
            if (now - _lastPollAt < POLL_MS) return;
            _lastPollAt = now;
            _pollOnce();
        });
    }
}

// Effective mode. Non-native is always ORIGINAL. On native, before the first
// poll resolves AND with no persisted value, we default to TRACKLIST_FALLBACK
// so cold start never flashes the original cover (the shipped intent is to
// substitute); once loaded, the real server mode applies.
function _effectiveMode() {
    if (!isNative) return 'ORIGINAL';
    if (_mode) return _mode;
    return _loaded ? 'ORIGINAL' : 'TRACKLIST_FALLBACK';
}

export function getNativeMixCoverMode() {
    return _effectiveMode();
}

// Subscribe a component to mode changes so its resolveMixCover() calls
// re-evaluate when the server flag loads/flips. Returns the current mode.
export function useNativeMixCoverMode() {
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!isNative) return undefined;
        const fn = () => setTick(n => n + 1);
        _subs.add(fn);
        return () => { _subs.delete(fn); };
    }, []);
    return _effectiveMode();
}

const _originalCover = (mix) => mix?.artworkUrl || mix?.artwork || mix?.image || null;

// First tracklist cover available on the mix object. The server pre-derives
// `coverFromTrack`; we also fall back to scanning a shipped tracklist /
// tracksIncluded (and snake_case for liked-mix rows from user-library).
export function firstTrackCover(mix) {
    if (!mix) return null;
    if (mix.coverFromTrack) return mix.coverFromTrack;
    if (mix.cover_from_track) return mix.cover_from_track;
    const items = mix.tracklist || mix.tracksIncluded;
    if (Array.isArray(items)) {
        for (const it of items) {
            const url = it?.track?.artworkUrl || it?.track?.artwork;
            if (url) return url;
        }
    }
    return null;
}

// THE single mix-cover resolver. Use this everywhere a native surface renders
// a mix cover. Web/desktop/mobile-web get the original cover unchanged.
export function resolveMixCover(mix) {
    const original = _originalCover(mix);
    if (!isNative) return original;
    const mode = _effectiveMode();
    if (mode === 'ORIGINAL') return original;
    const fromTrack = firstTrackCover(mix);
    if (fromTrack) return fromTrack;
    return mode === 'TRACKLIST_STRICT' ? null : original;
}
